/**
 * AdminNewProfilesModal.tsx
 *
 * File-level:
 * - Modal component to display today's new profiles for admin users.
 * - Visual design:
 *   - Full-screen project-themed gradient backdrop (uses theme CSS variables).
 *   - Bold gradient title bar with title, count pill and action buttons.
 *   - Table header with complementary gradient and high-contrast text.
 *
 * Notes:
 * - Fetches data from GET /api/admin/new-profiles when opened.
 * - Client-side filtering by User ID, First Name and Mobile (immediate filtering).
 * - Accessibility attributes (role, aria-*).
 * - Written in TypeScript and contains jsdoc comments for file, components and functions.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { API_CONFIG, getMediaUrl } from '../../config/api.config';

const API_URL = API_CONFIG.getBackendOrigin();

/**
 * ProfileRow
 *
 * @description Minimal shape of profile object returned by the backend for new-profiles.
 */
export interface ProfileRow {
  /** Unique user identifier */
  userId: string;
  /** First name of the user */
  firstName: string;
  /** Last name of the user */
  lastName: string;
  /** Mobile number (optional) */
  mobileNo?: string;
  /** Email (optional) */
  email?: string;
  /** Photo URL (may be absolute or null) */
  photoUrl?: string | null;
}

/**
 * AdminNewProfilesModalProps
 *
 * @description Props for AdminNewProfilesModal component.
 */
interface AdminNewProfilesModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called to close the modal */
  onClose: () => void;
}

/**
 * useFilteredProfiles
 *
 * @description Filters profiles by userId, firstName and mobile/email fields.
 * @param profiles Array of ProfileRow
 * @param filters Filter values
 * @returns Filtered array of ProfileRow
 */
function useFilteredProfiles(
  profiles: ProfileRow[],
  filters: { userId: string; firstName: string; mobile: string }
) {
  return useMemo(() => {
    const uid = filters.userId.trim().toLowerCase();
    const fn = filters.firstName.trim().toLowerCase();
    const mb = filters.mobile.trim().toLowerCase();

    if (!uid && !fn && !mb) return profiles;

    return profiles.filter((p) => {
      const matchesUserId = uid ? String(p.userId || '').toLowerCase().includes(uid) : true;
      const matchesFirstName = fn ? String(p.firstName || '').toLowerCase().includes(fn) : true;
      const matchesMobile = mb ? String((p.mobileNo || p.email || '')).toLowerCase().includes(mb) : true;
      return matchesUserId && matchesFirstName && matchesMobile;
    });
  }, [profiles, filters]);
}

/**
 * AdminNewProfilesModal
 *
 * @description Modal UI that lists new profiles and provides search + view actions.
 * Visuals:
 * - Full-screen themed backdrop using project CSS variables.
 * - Prominent gradient title bar (var(--card-gradient-new)) and complementary table header (var(--card-gradient-total)).
 */
export default function AdminNewProfilesModal({ open, onClose }: AdminNewProfilesModalProps) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({ userId: '', firstName: '', mobile: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    let mounted = true;

    /**
     * fetchNewProfiles
     *
     * @description Fetch today's new profiles from backend API.
     */
    async function fetchNewProfiles() {
      setError(null);
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/new-profiles`);
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`${res.status} ${res.statusText} - ${txt}`);
        }
        const json = await res.json();
        const list = Array.isArray(json.list) ? json.list : json.list || [];
        if (mounted) setProfiles(list);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[AdminNewProfilesModal] fetch error', err);
        if (mounted) setError(err?.message ?? 'Failed to fetch new profiles');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (open) {
      fetchNewProfiles();
      // Reset filters each time modal is opened for clarity
      setFilters({ userId: '', firstName: '', mobile: '' });
      setCurrentPage(1);
    }

    return () => {
      mounted = false;
    };
  }, [open]);

  const filtered = useFilteredProfiles(profiles, filters);

  // Calculate pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProfiles = filtered.slice(startIndex, endIndex);

  /**
   * handleView
   *
   * @description Navigate to selected profile within the app.
   * @param userId Selected profile userId
   */
  function handleView(userId: string) {
    onClose();
    setTimeout(() => {
      navigate(`/profile/${encodeURIComponent(userId)}`);
    }, 100);
  }

  /**
   * onFilterChange
   *
   * @description Update a filter field.
   * @param key Filter key
   * @param value New string value
   */
  function onFilterChange(key: keyof typeof filters, value: string) {
    setFilters((s) => ({ ...s, [key]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New profiles modal"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8"
    >
      {/* Full-screen themed backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(2,6,23,0.75), rgba(7,16,26,0.75)), var(--dashboard-bg, linear-gradient(135deg,#8694a1 0%,#ecfeff 100%))',
          backdropFilter: 'blur(6px)',
        }}
      />

      {/* Modal container */}
      <div className="relative w-full max-w-6xl rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10">
        {/* Title bar */}
        <div
          className="px-6 py-5 flex items-center justify-between gap-4"
          style={{
            backgroundImage: 'var(--card-gradient-new)',
            color: 'white',
          }}
        >
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold leading-tight">New Profiles — Today</h3>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full bg-white/12 text-sm font-medium"
              aria-hidden="true"
              title={`${profiles.length} profiles`}
            >
              {profiles.length}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFilters({ userId: '', firstName: '', mobile: '' })}
              className="text-sm text-white/90 px-3 py-1 rounded bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close new profiles modal"
              className="text-sm text-white/90 px-3 py-1 rounded bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <div>
              <label htmlFor="filter-userid" className="block text-sm font-medium text-slate-700 mb-1">
                User ID
              </label>
              <input
                id="filter-userid"
                value={filters.userId}
                onChange={(e) => onFilterChange('userId', e.target.value)}
                placeholder="Search by User ID"
                className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>

            <div>
              <label htmlFor="filter-firstname" className="block text-sm font-medium text-slate-700 mb-1">
                First Name
              </label>
              <input
                id="filter-firstname"
                value={filters.firstName}
                onChange={(e) => onFilterChange('firstName', e.target.value)}
                placeholder="Search by First Name"
                className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>

            <div>
              <label htmlFor="filter-mobile" className="block text-sm font-medium text-slate-700 mb-1">
                Mobile / Email
              </label>
              <input
                id="filter-mobile"
                value={filters.mobile}
                onChange={(e) => onFilterChange('mobile', e.target.value)}
                placeholder="Search by Mobile or Email"
                className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
          </div>

          {/* List */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr
                  className="text-left"
                  style={{
                    backgroundImage: 'var(--card-gradient-total, linear-gradient(90deg,#7C3AED 0%,#EC4899 100%))',
                    color: 'white',
                  }}
                >
                  <th className="py-3 pr-4 pl-4 w-24">DP</th>
                  <th className="py-3 pr-4 w-40">User ID</th>
                  <th className="py-3 pr-4">First Name</th>
                  <th className="py-3 pr-4">Last Name</th>
                  <th className="py-3 pr-4">Mobile / Email</th>
                  <th className="py-3 pr-4 w-28">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-red-500">
                      {error}
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      No new profiles found for today.
                    </td>
                  </tr>
                ) : (
                  paginatedProfiles.map((p) => (
                    <tr key={p.userId} className="border-t last:border-b-0">
                      <td className="py-3 pr-4 pl-4 align-middle">
                        {p.photoUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={getMediaUrl(p.photoUrl)}
                            alt={`${p.firstName} ${p.lastName}`}
                            className="w-12 h-12 rounded object-cover border"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-500 border">
                            N/A
                          </div>
                        )}
                      </td>

                      <td className="py-3 pr-4 font-mono align-middle">{p.userId}</td>
                      <td className="py-3 pr-4 align-middle">{p.firstName}</td>
                      <td className="py-3 pr-4 align-middle">{p.lastName}</td>
                      <td className="py-3 pr-4 align-middle">{p.mobileNo || p.email || '—'}</td>
                      <td className="py-3 pr-4 align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleView(p.userId)}
                            className="px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer summary */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Showing {startIndex + 1}–{Math.min(endIndex, filtered.length)} of {filtered.length} new profiles.
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <span className="text-sm font-medium text-slate-700">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
